/**
 * ★ Fase 44 — Chat widget DEDICATO ai funnel verticali.
 * Ogni funnel ha il suo assistente: endpoint /api/public/funnel-chat con
 * prompt limitato ai contenuti della landing/VSL di QUEL funnel.
 * Lead capture → /api/public/funnel-lead (lead taggato con l'acronimo in CRM).
 * Stile: palette del nuovo funnel (emerald scuro + oro + avorio).
 */
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { MessageCircle, X, Send, Loader2, CalendarCheck2 } from 'lucide-react';

const INK = '#081712';
const EMERALD = '#0C2B21';
const GOLD = '#D6B25E';
const IVORY = '#F4EEE1';
const SAND = '#CFC6B2';

const GREETINGS = {
  it: 'Ciao! Sono l\u2019Advisor HCF del percorso Albania. Dimmi una cosa: stai cercando una rendita, diversificazione o un piano B fuori dall\u2019Italia?',
  es: '\u00a1Hola! Soy el asesor HCF de este recorrido. Dime: \u00bfbuscas renta, diversificaci\u00f3n o un plan B?',
  en: 'Hi! I\u2019m the HCF advisor for this journey. Tell me: are you looking for yield, diversification or a plan B?',
};

const UI = {
  it: { title: 'Assistente HCF', ph: 'Scrivi un messaggio\u2026', leadTitle: 'Vuoi essere ricontattato?', name: 'Nome e cognome', phone: 'Telefono (consigliato)', send: 'Invia contatti', ok: (n) => `Perfetto ${n}! Ho passato i tuoi dati al team: ti ricontattiamo entro 24 ore. Posso aiutarti con altro?`, cta: 'Lascia i contatti', book: 'Prenota una consulenza strategica col CEO', close: 'Chiudi' },
  es: { title: 'Asistente HCF', ph: 'Escribe un mensaje\u2026', leadTitle: '\u00bfQuieres que te contactemos?', name: 'Nombre y apellidos', phone: 'Tel\u00e9fono (recomendado)', send: 'Enviar contactos', ok: (n) => `\u00a1Perfecto ${n}! Un asesor te contactar\u00e1 en 24 horas. \u00bfAlgo m\u00e1s?`, cta: 'Dejar contactos', book: 'Reserva la consultoría estratégica', close: 'Cerrar' },
  en: { title: 'HCF Assistant', ph: 'Type a message\u2026', leadTitle: 'Want us to contact you?', name: 'Full name', phone: 'Phone (recommended)', send: 'Send contacts', ok: (n) => `Great ${n}! An advisor will contact you within 24 hours. Anything else?`, cta: 'Leave contacts', book: 'Book a call with Richard Demichele', close: 'Close' },
};

export default function FunnelChatWidget({ funnel, language = 'it', bookingUrl = '/it/booking?rep=richard' }) {
  const t = UI[language] || UI.it;
  const [open, setOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [showLead, setShowLead] = useState(false);
  const [leadDone, setLeadDone] = useState(false);
  const [lName, setLName] = useState('');
  const [lEmail, setLEmail] = useState('');
  const [lPhone, setLPhone] = useState('');
  const scrollRef = useRef(null);
  const idRef = useRef(0);
  const nid = () => { idRef.current += 1; return `fm-${idRef.current}`; };
  const [sessionId, setSessionId] = useState(() => {
    try {
      const key = `hcf_funnel_chat_${funnel}`;
      let id = localStorage.getItem(key);
      if (!id) { id = `funnel-${funnel}-${Math.random().toString(36).slice(2)}`; localStorage.setItem(key, id); }
      return id;
    } catch { return `funnel-${funnel}-${Math.random().toString(36).slice(2)}`; }
  });

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [history.length, showLead]);

  const openChat = () => {
    setOpen(true);
    if (history.length === 0) setHistory([{ id: nid(), role: 'assistant', content: GREETINGS[language] || GREETINGS.it }]);
  };

  const send = async () => {
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    const next = [...history, { id: nid(), role: 'user', content: text }];
    setHistory(next);
    setDraft('');
    try {
      const r = await api.post('/public/funnel-chat', {
        funnel,
        session_id: sessionId,
        message: text,
        history: next.slice(0, -1).map((m) => ({ role: m.role, content: m.content })),
      });
      if (r.data?.session_id) setSessionId(r.data.session_id);
      setHistory((h) => [...h, {
        id: nid(), role: 'assistant', content: r.data?.reply || '...',
        booking: !!r.data?.suggest_booking,
        bookingHref: r.data?.booking_url || bookingUrl, // Calendly diretto (CRM) → fallback calendario interno
      }]);
      const userTurns = next.filter((m) => m.role === 'user').length;
      if (userTurns >= 2 && !leadDone && !showLead) setTimeout(() => setShowLead(true), 600);
    } catch {
      setHistory((h) => [...h, { id: nid(), role: 'assistant', content: 'Piccolo intoppo tecnico: riprova tra un attimo o lascia i contatti nel form in pagina.' }]);
    } finally {
      setSending(false);
    }
  };

  const submitLead = async () => {
    if (lName.trim().length < 2 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(lEmail.trim())) {
      toast.error('Nome ed email validi obbligatori');
      return;
    }
    try {
      await api.post('/public/funnel-lead', {
        funnel, name: lName.trim(), email: lEmail.trim(), phone: lPhone.trim() || null,
        message: 'Lead dalla chat del funnel',
      });
      setLeadDone(true);
      setShowLead(false);
      setHistory((h) => [...h, { id: nid(), role: 'assistant', content: t.ok(lName.trim().split(' ')[0]) }]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore, riprova');
    }
  };

  const inputStyle = {
    background: 'rgba(244,238,225,0.08)',
    border: `1px solid ${GOLD}4D`,
    color: IVORY,
    caretColor: GOLD,
  };

  return (
    <>
      <style>{`
        .hcf-fcw-input::placeholder { color: rgba(207,198,178,0.55); }
        .hcf-fcw-input { transition: border-color .2s, box-shadow .2s, background-color .2s; }
        .hcf-fcw-input:focus { border-color: ${GOLD} !important; box-shadow: 0 0 0 3px rgba(214,178,94,0.18); background: rgba(244,238,225,0.12) !important; }
      `}</style>
      {!open && (
        <button
          type="button"
          onClick={openChat}
          className="fixed bottom-5 right-4 md:bottom-7 md:right-6 z-[70] flex items-center gap-2 rounded-full px-4 py-3 shadow-2xl transition-transform duration-200 hover:scale-105 font-sans"
          style={{ background: GOLD, color: '#143C2B', boxShadow: `0 0 0 1px ${GOLD}, 0 14px 40px rgba(0,0,0,0.5)` }}
          data-testid="funnel-chat-open"
          aria-label={t.title}
        >
          <MessageCircle className="w-5 h-5" />
          <span className="text-xs font-bold tracking-[0.15em] uppercase hidden sm:inline">{t.title}</span>
        </button>
      )}

      {open && (
        <div
          className="fixed bottom-4 right-3 md:bottom-6 md:right-6 z-[80] w-[94vw] max-w-sm rounded-2xl border overflow-hidden flex flex-col font-sans"
          style={{ background: `${INK}F5`, borderColor: `${GOLD}66`, backdropFilter: 'blur(18px)', boxShadow: '0 30px 80px rgba(0,0,0,0.6)', height: 'min(560px, 78vh)' }}
          data-testid="funnel-chat-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b" style={{ background: EMERALD, borderColor: `${GOLD}44` }}>
            <div className="flex items-center gap-2.5">
              <span className="w-8 h-8 rounded-full grid place-items-center" style={{ background: GOLD }}>
                <MessageCircle className="w-4 h-4" style={{ color: '#143C2B' }} />
              </span>
              <div>
                <div className="text-sm font-semibold font-serif" style={{ color: IVORY }}>{t.title}</div>
                <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: GOLD }}>{String(funnel).toUpperCase()}</div>
              </div>
            </div>
            <button type="button" onClick={() => setOpen(false)} className="p-1.5 rounded-full hover:bg-white/10 transition-colors" data-testid="funnel-chat-close" aria-label={t.close}>
              <X className="w-4 h-4" style={{ color: SAND }} />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3.5 py-4 space-y-3">
            {history.map((m) => (
              <div key={m.id} className={`flex flex-col ${m.role === 'user' ? 'items-end' : 'items-start'}`}>
                <div
                  className="max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap"
                  style={m.role === 'user'
                    ? { background: GOLD, color: '#143C2B', borderBottomRightRadius: 6 }
                    : { background: EMERALD, color: IVORY, border: `1px solid ${GOLD}33`, borderBottomLeftRadius: 6 }}
                >
                  {m.content}
                </div>
                {m.booking && (
                  /^https?:\/\//.test(m.bookingHref || '') ? (
                    <a
                      href={m.bookingHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2 max-w-[85%] inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-[11px] font-bold tracking-[0.15em] uppercase transition-transform duration-150 hover:scale-[1.02]"
                      style={{ background: `linear-gradient(135deg, #E3C87A 0%, ${GOLD} 45%, #B8860B 100%)`, color: '#11271E', boxShadow: '0 8px 24px rgba(214,178,94,0.3)' }}
                      data-testid="funnel-chat-book-call"
                    >
                      <CalendarCheck2 className="w-4 h-4" /> {t.book}
                    </a>
                  ) : (
                    <Link
                      to={m.bookingHref || bookingUrl}
                      className="mt-2 max-w-[85%] inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-[11px] font-bold tracking-[0.15em] uppercase transition-transform duration-150 hover:scale-[1.02]"
                      style={{ background: `linear-gradient(135deg, #E3C87A 0%, ${GOLD} 45%, #B8860B 100%)`, color: '#11271E', boxShadow: '0 8px 24px rgba(214,178,94,0.3)' }}
                      data-testid="funnel-chat-book-call"
                    >
                      <CalendarCheck2 className="w-4 h-4" /> {t.book}
                    </Link>
                  )
                )}
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="rounded-2xl px-3.5 py-2.5" style={{ background: EMERALD, border: `1px solid ${GOLD}33` }}>
                  <Loader2 className="w-4 h-4 animate-spin" style={{ color: GOLD }} />
                </div>
              </div>
            )}
            {showLead && !leadDone && (
              <div className="rounded-xl border p-3.5 space-y-2.5" style={{ borderColor: `${GOLD}55`, background: `${EMERALD}CC` }} data-testid="funnel-chat-lead-form">
                <div className="text-sm font-semibold" style={{ color: IVORY }}>{t.leadTitle}</div>
                <input value={lName} onChange={(e) => setLName(e.target.value)} placeholder={`${t.name} *`} className="hcf-fcw-input w-full rounded-md px-3 py-2 text-sm outline-none" style={inputStyle} data-testid="funnel-chat-lead-name" />
                <input type="email" value={lEmail} onChange={(e) => setLEmail(e.target.value)} placeholder="Email *" className="hcf-fcw-input w-full rounded-md px-3 py-2 text-sm outline-none" style={inputStyle} data-testid="funnel-chat-lead-email" />
                <input value={lPhone} onChange={(e) => setLPhone(e.target.value)} placeholder={t.phone} className="hcf-fcw-input w-full rounded-md px-3 py-2 text-sm outline-none" style={inputStyle} data-testid="funnel-chat-lead-phone" />
                <button type="button" onClick={submitLead} className="w-full rounded-full h-10 text-[11px] font-bold tracking-[0.2em] uppercase transition-transform duration-150 hover:scale-[1.01]" style={{ background: GOLD, color: '#143C2B' }} data-testid="funnel-chat-lead-submit">
                  {t.send}
                </button>
              </div>
            )}
          </div>

          {/* Composer */}
          <div className="p-3 border-t flex items-center gap-2" style={{ borderColor: `${GOLD}33`, background: `${EMERALD}99` }}>
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && send()}
              placeholder={t.ph}
              className="hcf-fcw-input flex-1 rounded-full px-4 py-2.5 text-sm outline-none"
              style={inputStyle}
              data-testid="funnel-chat-input"
            />
            <button
              type="button" onClick={send} disabled={!draft.trim() || sending}
              className="w-10 h-10 rounded-full grid place-items-center transition-transform duration-150 hover:scale-105 disabled:opacity-50"
              style={{ background: GOLD, color: '#143C2B' }}
              data-testid="funnel-chat-send"
              aria-label="Invia"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
